import { useMemo } from "react";
import { MRT_ColumnDef } from "mantine-react-table";
import { Badge, Text, Button, Group } from "@mantine/core";
import type { RegtankLivenessHistoryData } from "../Regtank.types";
import { LivenessWebVerifyStatusEnum } from "@funded-here-interface/common/src/constant/enum";
import { useStyles } from "../Regtank.styles";

interface UseLivenessHistoryColumnsProps {
  getNewSubmittedOn: (requestId?: string, sessionId?: string) => boolean;
  getNewReviewedOn: (
    reviewedOn: string,
    requestId?: string,
    sessionId?: string
  ) => string | undefined;
  handleViewLiveness: (
    requestIdOrSessionId: string,
    submittedDate: string
  ) => boolean;
  handleViewPhotoVideoLiveness: (
    clientSessionId: string,
    sessionId: string,
    userId: string,
    verificationResult: LivenessWebVerifyStatusEnum,
    confidence: number,
    type: string
  ) => void;
  getLivenessHistoryVerificationResult: (
    sessionId: string,
    status: LivenessWebVerifyStatusEnum
  ) => string | undefined;
  getLivenessVerificationResultColor: (
    sessionId: string,
    status: LivenessWebVerifyStatusEnum
  ) => string | undefined;
  getNewFailureReason: (
    reason: string,
    requestId?: string,
    sessionId?: string
  ) => string[];
  getNewRemark: (
    remark: string,
    requestId?: string,
    sessionId?: string
  ) => string | undefined;
  getNewReattemptApproval: (
    reattemptApproval: string,
    requestId?: string,
    sessionId?: string
  ) => string | undefined;
  getReattemptApprovalColor: (reattemptApproval: string) => string | undefined;
}

/**
 * Note: Helper functions are passed as props from parent component.
 * The parent wraps utility functions from utils/regtankHelpers.ts with useCallback
 * to ensure stable references and prevent unnecessary re-renders.
 */

/**
 * Column definitions for liveness history table
 * Used for displaying detailed liveness verification history with photo/video checks
 */
export const useLivenessHistoryColumns = ({
  getNewSubmittedOn,
  getNewReviewedOn,
  handleViewLiveness,
  handleViewPhotoVideoLiveness,
  getLivenessHistoryVerificationResult,
  getLivenessVerificationResultColor,
  getNewFailureReason,
  getNewRemark,
  getNewReattemptApproval,
  getReattemptApprovalColor,
}: UseLivenessHistoryColumnsProps) => {
  const { classes } = useStyles();
  return useMemo<MRT_ColumnDef<RegtankLivenessHistoryData>[]>(
    () => [
      {
        accessorKey: "linkSentOn",
        header: "Link Generated On",
      },
      {
        accessorKey: "submittedDate",
        header: "Verification Submitted On",
        Cell: ({ row }) => {
          const label = getNewSubmittedOn("", row.original.sessionId);
          if (!label) return null;
          return <Text>{row.original.submittedDate}</Text>;
        },
      },
      {
        accessorKey: "reviewedOn",
        header: "Reviewed On",
        Cell: ({ row }) => {
          const label = getNewReviewedOn(
            row.original.reviewedOn || "",
            row.original.sessionId
          );
          if (!label) return null;
          return <Text>{label}</Text>;
        },
      },
      {
        id: "livenessPhoto",
        header: "Liveness Check",
        Cell: ({ row }) => (
          <Group>
            <Button
              disabled={handleViewLiveness(
                row.original.sessionId,
                row.original.submittedDate ?? ""
              )}
              onClick={() =>
                handleViewPhotoVideoLiveness(
                  row.original.clientSessionId,
                  row.original.sessionId,
                  row.original.userId,
                  row.original.verificationResult,
                  row.original.confidence || 0,
                  "PHOTO"
                )
              }
            >
              VIEW PHOTO
            </Button>
            <Button
              disabled={handleViewLiveness(
                row.original.sessionId,
                row.original.submittedDate ?? ""
              )}
              onClick={() =>
                handleViewPhotoVideoLiveness(
                  row.original.clientSessionId,
                  row.original.sessionId,
                  row.original.userId,
                  row.original.verificationResult,
                  row.original.confidence || 0,
                  "VIDEO"
                )
              }
            >
              VIEW VIDEO
            </Button>
          </Group>
        ),
      },
      {
        accessorKey: "verificationResult",
        header: "Verification Result",
        enableSorting: false,
        Cell: ({ row }) => {
          const label = getLivenessHistoryVerificationResult(
            row.original.sessionId,
            row.original.verificationResult
          );
          if (!label) return null;
          return (
            <Badge
              color={getLivenessVerificationResultColor(
                row.original.sessionId,
                row.original.verificationResult
              )}
            >
              {getLivenessHistoryVerificationResult(
                row.original.sessionId,
                row.original.verificationResult
              )}
            </Badge>
          );
        },
      },
      {
        accessorKey: "failureReason",
        header: "Failure Reason",
        Cell: ({ row }) => {
          const label = getNewFailureReason(
            row.original.failureReason || "",
            row.original.sessionId,
            ""
          );
          if (!Array.isArray(label) || label.length === 0) return null;
          if (label[0].startsWith("-") || label.length < 2) {
            return <Text>{label}</Text>;
          }
          return (
            <ul className={classes.reasonList}>
              {label.map((x, index) => (
                <li className={classes.reasonItem} key={index}>
                  {x}
                </li>
              ))}
            </ul>
          );
        },
        size: 400,
        enableSorting: false,
      },
      {
        accessorKey: "remark",
        header: "Remark",
        enableSorting: false,
        Cell: ({ row }) => {
          const label = getNewRemark(
            row.original.remark || "",
            row.original.sessionId
          );
          return <Text>{label}</Text>;
        },
      },
      {
        accessorKey: "reattemptApproval",
        header: "Reattempt Approval",
        enableSorting: false,
        Cell: ({ row }) => {
          const label = getNewReattemptApproval(
            row.original.reattemptApproval || "",
            row.original.sessionId
          );
          if (label === "") return null;
          else if (label === "-") return label;
          return (
            <Badge
              color={getReattemptApprovalColor(
                row.original.reattemptApproval || ""
              )}
            >
              {label}
            </Badge>
          );
        },
      },
      {
        accessorKey: "agreement",
        header: "Agreement",
        enableSorting: false,
      },
    ],
    [
      getLivenessHistoryVerificationResult,
      getLivenessVerificationResultColor,
      getNewFailureReason,
      getNewReattemptApproval,
      getNewRemark,
      getNewReviewedOn,
      getNewSubmittedOn,
      getReattemptApprovalColor,
      handleViewLiveness,
      handleViewPhotoVideoLiveness,
    ]
  );
};
